import { ArrowUpRight, MoveRight } from "lucide-react";
import { Line, Reveal, Overline } from "@/components/Kinetic";
import { useLang } from "@/i18n/LanguageContext";

const SHIFTS_EN = [
  { from: "WordPress", to: "Next.js" },
  { from: "Legacy platform", to: "Modern architecture" },
  { from: "Outdated presence", to: "Current digital experience" },
];

const SHIFTS_TR = [
  { from: "WordPress", to: "Next.js" },
  { from: "Eski platform", to: "Modern mimari" },
  { from: "Eski web deneyimi", to: "Güncel dijital deneyim" },
];

const SCOPE_EN = [
  {
    index: "01",
    title: "Modern web architecture",
    text: "The corporate site moved from WordPress to Next.js, with dedicated pages for products, categories and company information.",
  },
  {
    index: "02",
    title: "Brand-consistent design",
    text: "A digital presence that matches the weight of an established industrial brand.",
  },
  {
    index: "03",
    title: "Product information",
    text: "Product pages bring descriptions, specifications and a clear inquiry action together.",
  },
  {
    index: "04",
    title: "Built to evolve",
    text: "A foundation the business can extend as its digital needs grow.",
  },
];

const SCOPE_TR = [
  {
    index: "01",
    title: "Modern web mimarisi",
    text: "Kurumsal site WordPress’ten Next.js’e taşındı. Ürünler, kategoriler ve şirket bilgileri ayrı sayfalarda sunuluyor.",
  },
  {
    index: "02",
    title: "Markayla tutarlı tasarım",
    text: "Köklü bir endüstriyel markanın kimliğini yansıtan web tasarımı.",
  },
  {
    index: "03",
    title: "Ürün bilgileri",
    text: "Ürün sayfaları açıklamaları, teknik bilgileri ve iletişim adımını bir araya getiriyor.",
  },
  {
    index: "04",
    title: "Gelişmeye açık",
    text: "İşletmenin dijital ihtiyaçları büyüdükçe genişletebileceği bir temel.",
  },
];

export default function DosePage() {
  const { lang, tx } = useLang();
  const shifts = lang === "tr" ? SHIFTS_TR : SHIFTS_EN;
  const scope = lang === "tr" ? SCOPE_TR : SCOPE_EN;


  return (
    <>
      <section className="bg-paper pt-32 sm:pt-40 lg:pt-44">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <Reveal immediate y={16}>
                <Overline>{tx("GADULABS — Corporate web experience", "GADULABS — Kurumsal web deneyimi")}</Overline>
              </Reveal>
              <h1
                data-testid="dose-hero-title"
                className="mt-7 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              >
                {lang === "tr" ? (
                  <>
                    <Line delay={0.1}>Köklü bir işletme için</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">modern bir web deneyimi.</span>
                    </Line>
                  </>
                ) : (
                  <>
                    <Line delay={0.1}>A modern web presence</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">for an established business.</span>
                    </Line>
                  </>
                )}
              </h1>
              <Reveal immediate delay={0.45} y={20} className="mt-7 max-w-xl">
                <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                  {tx(
                    "DOSE Modernization is the redevelopment of the corporate web presence of Atlantik Tüketim Malları Tic. ve San. Ltd. Şti. and its industrial aerosol brand DOSE — moving from an aging platform to a modern, maintainable digital experience.",
                    "DOSE Modernization; Atlantik Tüketim Malları Tic. ve San. Ltd. Şti. ve endüstriyel aerosol markası DOSE'un kurumsal web sitesinin yenilenmesidir — eskiyen bir platformdan modern ve sürdürülebilir bir dijital deneyime geçiş.",
                  )}
                </p>
              </Reveal>
              <Reveal immediate delay={0.6} y={20} className="mt-10">
                <a
                  href="https://dose.com.tr"
                  data-testid="dose-hero-cta"
                  className="focus-ring group inline-flex items-center gap-2 bg-ink px-6 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-navy"
                >
                  {tx("Visit the website", "Siteyi ziyaret edin")}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </a>
              </Reveal>
            </div>
            <Reveal immediate delay={0.5} y={32} className="lg:col-span-5">
              <div
                data-testid="dose-shift-panel"
                className="border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(11,15,23,0.25)] sm:p-8"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    {tx("Transformation map", "Dönüşüm haritası")}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="h-1.5 w-1.5 bg-gold" aria-hidden="true" />
                    DOSE
                  </span>
                </div>
                <div className="mt-8 space-y-3.5">
                  {shifts.map((s, i) => (
                    <div
                      key={s.from}
                      data-testid={`dose-shift-${i + 1}`}
                      className="flex items-center justify-between gap-4 border border-slate-200 bg-paper px-4 py-3.5"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        {s.from}
                      </span>
                      <MoveRight size={14} className="shrink-0 text-gold" aria-hidden="true" />
                      <span className="text-right font-display text-sm font-bold tracking-tight text-ink">
                        {s.to}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Overline>{tx("Live project", "Yayındaki proje")}</Overline>
          <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl">{tx("From brand to product detail.", "Markadan ürün detayına.")}</h2>
          <p className="mt-5 max-w-2xl text-slate-600">{tx("The live website brings the DOSE brand, industrial sprays and ironing board covers into one corporate experience. These are screenshots of the actual site, captured on 10 September 2026.", "Yayındaki site; DOSE markasını, endüstriyel spreyleri ve ütü masası kılıflarını tek bir kurumsal deneyimde bir araya getiriyor. Görseller 10 Eylül 2026’da gerçek siteden alındı.")}</p>
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <figure><a href="https://dose.com.tr" className="focus-ring block border border-slate-200"><img src="/images/dose-home.png" width="1280" height="720" loading="lazy" alt={tx("DOSE website homepage with its two product categories", "İki ana ürün kategorisinin yer aldığı DOSE ana sayfası")} /></a><figcaption className="mt-3 font-mono text-xs text-slate-500">{tx("01 — Homepage and product categories", "01 — Ana sayfa ve ürün kategorileri")}</figcaption></figure>
            <figure><a href="https://dose.com.tr/tr/urunler/dose-leke-cikartici-sprey" className="focus-ring block border border-slate-200"><img src="/images/dose-product.png" width="1280" height="720" loading="lazy" alt={tx("DOSE stain remover product page with specifications", "Teknik bilgileriyle DOSE leke çıkartıcı sprey ürün sayfası")} /></a><figcaption className="mt-3 font-mono text-xs text-slate-500">{tx("02 — Product information and inquiry", "02 — Ürün bilgileri ve iletişim")}</figcaption></figure>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline>{tx("Positioning", "Konumlandırma")}</Overline>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <h2 className="font-display text-3xl font-bold leading-snug tracking-tight text-ink sm:text-4xl">
                  {tx(
                    "A corporate website built around real products.",
                    "Gerçek ürünler için kurumsal bir web deneyimi.",
                  )}
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  {tx(
                    "DOSE Modernization is a corporate website and digital presence project. It demonstrates GADULABS' ability to design and build modern digital experiences for an established, operating business — a company that manufactures and exports physical goods.",
                    "DOSE Modernization bir kurumsal web deneyimi projesidir. GADULABS'ın köklü ve faal bir işletme — fiziksel ürünler üreten ve ihraç eden bir şirket — için modern dijital deneyimler tasarlama ve geliştirme yeteneğini gösterir.",
                  )}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-paper py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("Scope", "Kapsam")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              {tx("What modernization means here.", "Burada modernizasyonun anlamı.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {scope.map((w, i) => (
              <Reveal key={w.index} delay={i * 0.07} y={22} className="bg-paper">
                <div
                  data-testid={`dose-workstream-${w.index}`}
                  className="flex h-full flex-col p-7 transition-colors duration-300 hover:bg-white"
                >
                  <span className="font-mono text-xs tracking-[0.15em] text-gold">
                    {w.index}
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight text-ink">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {w.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 sm:py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline dark>GADULABS</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
              {tx("Explore the live DOSE website.", "Yayındaki DOSE sitesini inceleyin.")}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              {tx(
                "Browse the corporate pages, product catalog and technical details directly on dose.com.tr.",
                "Kurumsal sayfaları, ürün kataloğunu ve teknik bilgileri doğrudan dose.com.tr üzerinden inceleyin.",
              )}
            </p>
          </Reveal>
          <Reveal delay={0.28} className="mt-10">
            <a
              href="https://dose.com.tr"
              data-testid="dose-final-cta"
              className="focus-ring group inline-flex items-center gap-2 bg-white px-7 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:bg-cobalt hover:text-white"
            >
              {tx("Visit dose.com.tr", "dose.com.tr’ye gidin")}
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
